import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Download, MoreHorizontal, Upload, Eye, Pencil, Trash2, ChevronDown } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { apiCustomers } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import CustomerFormDialog from '../forms/CustomerFormDialog';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { removeMany, patchMany, confirmCount } from '../../lib/bulkRows';
import { toast } from 'sonner';

const typeColor = {
  Residential: 'bg-blue-100 text-blue-700',
  Commercial: 'bg-teal-100 text-teal-700',
  'Multi-Unit': 'bg-purple-100 text-purple-700',
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiCustomers.list().then((d) => setItems(d || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Search across everything a customer is actually identified by in the field —
  // a phone number or a street are as common a starting point as the account
  // name, and matching only name/id/account made those look like no such customer.
  const filtered = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    const termDigits = term.replace(/\D/g, '');
    return items.filter((c) => {
      const hay = [c.name, c.company, c.id, c.accountNumber, c.email,
                   c.address, c.fullAddress, c.city, c.state, c.zip]
        .filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(term)) return true;
      // Phone match ignores punctuation on both sides.
      return termDigits.length >= 3 &&
             String(c.phone || '').replace(/\D/g, '').includes(termDigits);
    });
  }, [items, q]);

  // Ticking rows. `filtered` is what the table paints, so a bulk action follows
  // the search: narrowing it narrows what gets deleted.
  const sel = useSelection(filtered.map((c) => c.id));
  const picked = () => filtered.filter((c) => sel.has(c.id));

  const bulkDelete = async () => {
    const ids = sel.chosen;
    if (!ids.length) return;
    if (!confirmCount('Delete', ids.length, 'customer',
                      'This cannot be undone. Their invoices and jobs stay on file.')) return;
    await removeMany(apiCustomers, ids, 'customer');
    sel.clear();
    load();
  };

  const bulkStatus = async (status) => {
    const rows = picked();
    if (!rows.length) return;
    await patchMany(apiCustomers, rows, { status }, `customer(s) set to ${status}`);
    sel.clear();
    load();
  };

  const exportPicked = () => {
    const rows = picked();
    if (!rows.length) return;
    downloadFile('xpertpests-customers-selected.csv', toCSV(rows, [
      { key: 'id', header: 'ID' }, { key: 'accountNumber', header: 'Accountnumber' },
      { key: 'name', header: 'Name' }, { key: 'email', header: 'Email' },
      { key: 'phone', header: 'Phone' }, { key: 'address', header: 'Address' },
      { key: 'type', header: 'Type' }, { key: 'balance', header: 'Balance' },
      { key: 'status', header: 'Status' }]));
    toast.success(`Exported ${rows.length} customer(s)`);
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const parsed = parseCSV(await readFileAsText(f));
    const docs = parsed.map((r, i) => ({ id: r.ID || r.Id || `C-imp-${Date.now()}-${i}`, accountNumber: r.Accountnumber || r['Account Number'] || '', name: r.Name || '', email: r.Email || '', phone: r.Phone || '', type: r.Type || 'Residential', balance: Number(r.Balance) || 0, status: r.Status || 'Active' }));
    try { await apiCustomers.bulk(docs); load(); toast.success(`Imported ${docs.length} customers`); } catch { toast.error('Import failed'); }
    e.target.value = '';
  };
  const onExport = () => {
    const csv = toCSV(items, [{ key: 'id', header: 'ID' }, { key: 'accountNumber', header: 'Accountnumber' }, { key: 'name', header: 'Name' }, { key: 'email', header: 'Email' }, { key: 'type', header: 'Type' }, { key: 'balance', header: 'Balance' }, { key: 'status', header: 'Status' }]);
    downloadFile('xpertpests-customers.csv', csv); toast.success('Exported customers.csv');
  };
  const onDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try { await apiCustomers.remove(c.id); load(); toast.success('Customer deleted'); } catch { toast.error('Delete failed'); }
  };
  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c) => { setEditing(c); setFormOpen(true); };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Customers</h1>
          <p className="text-slate-500 mt-1">{items.length} customers loaded</p>
        </div>
        <div className="flex gap-2">
          <label className="inline-flex items-center gap-1.5 px-4 h-10 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-4 h-4" />Import CSV<input type="file" accept=".csv" className="hidden" onChange={onImport} /></label>
          <Button variant="outline" onClick={onExport} className="gap-1.5"><Download className="w-4 h-4" />Export</Button>
          <Button onClick={openNew} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="add-customer-btn"><Plus className="w-4 h-4" />Add Customer</Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, account #, phone, email or address…" className="pl-9 h-10" data-testid="customer-search-input" />
          </div>
          <Button variant="outline" className="gap-1.5"><Filter className="w-4 h-4" />Filters</Button>
        </div>
        <BulkBar count={sel.count} onClear={sel.clear} testId="customers-bulk-bar"
                 countTestId="customers-bulk-count">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" data-testid="customers-bulk-status-btn">
                Set status <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {['Active', 'Inactive'].map((st) => (
                <DropdownMenuItem key={st} onClick={() => bulkStatus(st)} data-testid={`customers-bulk-status-${st}`}>
                  Mark as {st}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" onClick={exportPicked} data-testid="customers-bulk-export-btn">
            <Download className="w-3.5 h-3.5" />Export
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1 bg-white text-red-600 hover:text-red-700" onClick={bulkDelete} data-testid="customers-bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5" />Delete
          </Button>
        </BulkBar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left w-10">
                  <TickBox checked={sel.allPicked} onChange={sel.toggleAll}
                           label="Select every customer listed" testId="customers-select-all"/>
                </th>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Balance</th>
                <th className="px-5 py-3 text-left">Email</th>
                <th className="px-5 py-3 text-left">Location</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => {
                const bal = typeof c.balance === 'number' ? c.balance : Number((c.balance || '0').toString().replace(/[$,]/g, ''));
                return (
                  <tr key={c.id} className={`hover:bg-slate-50 transition ${sel.has(c.id) ? 'bg-[#8BC53F]/5' : ''}`} data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-4">
                      <TickBox checked={sel.has(c.id)} onChange={() => sel.toggle(c.id)}
                               label={`Select ${c.name || c.id}`} testId={`customers-select-${c.id}`}/>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => navigate(`/app/customers/${c.id}`)} className="font-semibold text-slate-900 hover:text-[#6FA82C] hover:underline text-left" data-testid={`customer-name-${c.id}`}>{c.name}</button>
                      <div className="text-xs text-slate-500 font-mono">{c.accountNumber ? `#${c.accountNumber}` : c.id}</div>
                    </td>
                    <td className="px-5 py-4"><Badge className={`${typeColor[c.type] || 'bg-slate-100 text-slate-700'} border-0`}>{c.type || '-'}</Badge></td>
                    <td className="px-5 py-4">
                      <span className={bal === 0 ? 'text-slate-500' : bal > 0 ? 'font-semibold text-red-600' : 'font-semibold text-emerald-600'}>${bal.toFixed(2)}</span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 text-xs">{c.email || '-'}</td>
                    <td className="px-5 py-4 text-slate-600 text-xs">{c.city ? `${c.city}, ${c.state || ''}` : '-'}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${c.status === 'Active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />{c.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="text-slate-400 hover:text-slate-700 p-1" data-testid={`customer-menu-${c.id}`}><MoreHorizontal className="w-4 h-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => navigate(`/app/customers/${c.id}`)}><Eye className="w-4 h-4 mr-2" />View details</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(c)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onDelete(c)} className="text-red-600 focus:text-red-700"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-500">No customers found. <button onClick={openNew} className="text-[#6FA82C] font-semibold hover:underline">Add your first customer</button>.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <CustomerFormDialog open={formOpen} setOpen={setFormOpen} editing={editing} onSaved={load} />
    </div>
  );
}
